/**
 * Build slim series days from orders_daily rows — demo fixtures only. Live
 * figures always come from the stored per-day summaries (`/daily/series`),
 * computed server-side by buildOrdersDailySeries. Chart metrics must match
 * buildTrendBarGroups(rows).
 */

import { ratioPercent } from '../lib/calc';
import type {
  OperationalProgress,
  OrdersDailyRow,
  OrdersDailySeriesDay,
} from '../types/ordersDailyTruth';
import { summariseEtaFromDeliveries } from './ordersDailyEtaSummary';
import { addUtcDays } from './ordersDailyPresentation';

function measure(rows: readonly OrdersDailyRow[]): { consignments: number; lifts: number } {
  const consignments = new Set(rows.map((r) => r.tracking_id));
  const lifts = rows.reduce((sum, r) => sum + (r.lifts ?? 0), 0);
  return { consignments: consignments.size, lifts };
}

function progress(
  rows: readonly OrdersDailyRow[],
  isComplete: (r: OrdersDailyRow) => boolean,
  isPartial?: (r: OrdersDailyRow) => boolean,
): OperationalProgress {
  const total = measure(rows);
  const completed = measure(rows.filter(isComplete));
  const open = measure(rows.filter((r) => r.open));
  const partial = isPartial ? measure(rows.filter(isPartial)) : undefined;
  return {
    total,
    completed,
    open,
    ...(partial ? { partial } : {}),
    completion_pct: ratioPercent(completed.consignments, total.consignments),
    target: { consignments: null, lifts: null },
  };
}

function eachDate(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  let d = startDate;
  while (d <= endDate) {
    out.push(d);
    d = addUtcDays(d, 1);
  }
  return out;
}

/** Group in-range rows by grain_date into series days (empty days omitted). */
export function seriesDaysFromOrdersRows(
  rows: readonly OrdersDailyRow[],
  startDate: string,
  endDate: string,
): OrdersDailySeriesDay[] {
  const byDate = new Map<string, OrdersDailyRow[]>();
  for (const row of rows) {
    if (row.grain_date < startDate || row.grain_date > endDate) continue;
    const list = byDate.get(row.grain_date);
    if (list) list.push(row);
    else byDate.set(row.grain_date, [row]);
  }

  const days: OrdersDailySeriesDay[] = [];
  for (const date of eachDate(startDate, endDate)) {
    const dayRows = byDate.get(date);
    if (!dayRows?.length) continue;
    const collections = dayRows.filter((r) => r.direction === 'collection');
    const deliveries = dayRows.filter((r) => r.direction === 'delivery');
    days.push({
      date,
      summary: {
        row_count: dayRows.length,
        collections: progress(collections, (r) => r.collected),
        deliveries: progress(
          deliveries,
          (r) => r.delivered,
          (r) => Boolean(r.partial_delivery),
        ),
        eta: summariseEtaFromDeliveries(deliveries),
      },
    });
  }
  return days;
}
